/* AssessmentSheet — full Arabic audit page for a single ATS candidate.
 *
 * Route: /ats/sheet/:aid
 *
 * Renders an A4-style printable audit page showing every question, every
 * answer, every demo task field (expected vs. entered) and the behaviour
 * log. Designed to be opened in its own tab from the ATS dashboard.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/api/client";

const C = {
  bg:        "#f6f1e7",
  paper:     "#ffffff",
  ink:       "#1f2937",
  muted:     "#6b7280",
  border:    "#e5e7eb",
  accent:    "#b8893a",
  accentDk:  "#8a6321",
  ok:        "#15803d",
  okBg:      "#dcfce7",
  bad:       "#b91c1c",
  badBg:     "#fee2e2",
  miss:      "#a16207",
  missBg:    "#fef3c7",
  rowAlt:    "#fbf6ec",
};

function StatusPill({ status }) {
  const map = {
    ok:     { ar: "صحيح", fg: C.ok,   bg: C.okBg },
    wrong:  { ar: "خطأ",  fg: C.bad,  bg: C.badBg },
    missing:{ ar: "ناقص", fg: C.miss, bg: C.missBg },
    correct:{ ar: "صحيح", fg: C.ok,   bg: C.okBg },
    missed: { ar: "لم يجب", fg: C.miss, bg: C.missBg },
  };
  const s = map[status] || { ar: "—", fg: C.muted, bg: "#f3f4f6" };
  return (
    <span
      data-testid={`pill-${status}`}
      style={{
        display: "inline-block", padding: "2px 10px", borderRadius: 999,
        background: s.bg, color: s.fg, fontWeight: 800, fontSize: 11.5,
        border: `1px solid ${s.fg}33`,
      }}
    >{s.ar}</span>
  );
}

const fmtTime = (sec) => {
  if (!sec) return "—";
  const m = Math.floor(sec / 60), s = sec % 60;
  return m ? `${m}د ${s}ث` : `${s}ث`;
};
const fmtISO = (s) => (s || "").replace("T", " ").slice(0, 19) || "—";

export default function AssessmentSheet() {
  const { aid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    api.get(`/ats/applicants/${aid}/assessment-sheet`)
      .then(({ data }) => { if (mounted) setData(data); })
      .catch((e) => { if (mounted) setErr(e?.response?.data?.detail || "تعذّر تحميل الورقة"); });
    return () => { mounted = false; };
  }, [aid]);

  if (err) return <div style={{ padding: 40, color: C.bad, direction: "rtl" }} data-testid="sheet-error">{err}</div>;
  if (!data) return <div style={{ padding: 40, color: C.muted, direction: "rtl" }} data-testid="sheet-loading">جارٍ تحميل ورقة التقييم…</div>;

  const { applicant, summary, initial_standard, initial_popups, tasks, behaviour } = data;
  const recColor =
    summary.recommendation_ar === "قبول مرشّح" ? C.ok :
    summary.recommendation_ar === "رفض"         ? C.bad :
                                                   C.miss;

  const page = {
    maxWidth: 980, margin: "0 auto", background: C.paper,
    border: `1px solid ${C.border}`, borderRadius: 14, padding: 28,
    boxShadow: "0 8px 30px rgba(0,0,0,0.04)",
    color: C.ink, fontFamily: "system-ui, -apple-system, 'Segoe UI', Tahoma, sans-serif",
    direction: "rtl",
  };
  const h2 = {
    fontSize: 17, fontWeight: 900, color: C.accentDk, marginTop: 28, marginBottom: 12,
    borderBottom: `2px solid ${C.accent}`, paddingBottom: 6,
  };
  const meta = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13, color: C.muted };
  const metaVal = { color: C.ink, fontWeight: 700 };

  const printPage = () => window.print();

  return (
    <div style={{ minHeight: "100vh", background: C.bg, padding: "24px 12px" }} data-testid="assessment-sheet">
      <div style={{ maxWidth: 980, margin: "0 auto 14px", display: "flex", gap: 8, justifyContent: "space-between" }} className="no-print">
        <button onClick={() => (window.opener ? window.close() : navigate(-1))}
                style={btnLight} data-testid="sheet-back">‹ رجوع</button>
        <button onClick={printPage} style={btnAccent} data-testid="sheet-print">طباعة / حفظ PDF</button>
      </div>

      <div style={page}>
        {/* ── Header ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 800, letterSpacing: 1.5 }}>ورقة تقييم المرشّح</div>
            <div style={{ fontSize: 24, fontWeight: 900, marginTop: 4 }} data-testid="sheet-name">{applicant.full_name}</div>
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 4 }}>
              {applicant.country}{applicant.city ? ` • ${applicant.city}` : ""}
              {applicant.email ? ` • ${applicant.email}` : ""}
              {applicant.mobile ? ` • ${applicant.mobile}` : ""}
            </div>
            {applicant.referral_source && (
              <div style={{ fontSize: 12, color: C.accentDk, marginTop: 6, fontWeight: 700 }} data-testid="sheet-referral">
                مصدر المعرفة بالرابط: {({ facebook: "فيسبوك", linkedin: "لينكدإن", whatsapp: "واتساب", other: "أخرى" })[applicant.referral_source] || applicant.referral_source}
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }} data-testid="sheet-experience">
              <span style={{
                padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                background: applicant.has_visa_experience ? "#dcfce7" : "#fee2e2",
                color: applicant.has_visa_experience ? C.ok : C.bad,
                border: `1px solid ${applicant.has_visa_experience ? C.ok : C.bad}`,
              }}>
                {applicant.has_visa_experience
                  ? `✓ خبرة تأشيرات${applicant.years_experience > 0 ? ` · ${applicant.years_experience} سنة` : ""}`
                  : "✗ بدون خبرة تأشيرات"}
              </span>
              {applicant.english_level && <span style={{
                padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                background: `${C.accent}22`, color: C.accentDk, border: `1px solid ${C.accent}`,
              }}>
                مستوى الإنجليزية: {({ fluent: "طلق", good: "جيد", basic: "أساسي" })[applicant.english_level] || applicant.english_level}
              </span>}
            </div>
          </div>
          <div style={{
            padding: "10px 16px", borderRadius: 12, border: `2px solid ${recColor}`,
            background: `${recColor}11`, textAlign: "center", minWidth: 140,
          }} data-testid="sheet-recommendation">
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 800 }}>التوصية</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: recColor, marginTop: 2 }}>
              {summary.recommendation_ar}
            </div>
          </div>
        </div>

        {/* ── Summary ── */}
        <div style={h2}>الملخّص العام</div>
        <div style={meta}>
          <div>النتيجة النهائية: <span style={metaVal} data-testid="sheet-final-score">{summary.initial_score} / 100</span></div>
          <div>الحالة: <span style={{ ...metaVal, color: summary.status_ar === "اجتاز" ? C.ok : C.bad }} data-testid="sheet-status">{summary.status_ar}</span></div>
          <div>السبب: <span style={metaVal}>{summary.reason_ar}</span></div>
          <div>الديمو العملي: <span style={metaVal}>{summary.demo_ar}</span></div>
          <div>تاريخ التقديم: <span style={metaVal}>{fmtISO(applicant.created_at)}</span></div>
          <div>تاريخ إنهاء الاختبار: <span style={metaVal}>{fmtISO(applicant.test_completed_at)}</span></div>
        </div>

        {/* ── Standard Questions ── */}
        <div style={h2}>أولاً: الاختبار الأولي ({initial_standard.length} أسئلة)</div>
        {initial_standard.length === 0
          ? <div style={{ color: C.muted, fontSize: 13 }}>لم يبدأ الاختبار الأولي.</div>
          : <table style={tableSty} data-testid="sheet-std-table">
              <thead>
                <tr>
                  <th style={th}>#</th>
                  <th style={th}>السؤال</th>
                  <th style={th}>نوع</th>
                  <th style={th}>إجابة المرشح</th>
                  <th style={th}>الإجابة الصحيحة</th>
                  <th style={th}>النتيجة</th>
                  <th style={th}>الوقت</th>
                  <th style={th}>ملاحظات</th>
                </tr>
              </thead>
              <tbody>
              {initial_standard.map((q, i) => {
                const notes = [];
                if (q.fast_guess) notes.push("إجابة سريعة جداً");
                if (q.behaviour?.tab_switches) notes.push(`تبويب×${q.behaviour.tab_switches}`);
                if (q.behaviour?.pastes)       notes.push(`لصق×${q.behaviour.pastes}`);
                return (
                  <tr key={q.id} style={{ background: i % 2 ? C.rowAlt : C.paper }}>
                    <td style={td}>{q.order}</td>
                    <td style={{ ...td, fontWeight: 700 }}>
                      <div style={{ color: C.accentDk, fontSize: 11.5, fontWeight: 800 }}>{q.title_ar}</div>
                      <div style={{ color: C.ink, marginTop: 2 }}>{q.question_ar}</div>
                    </td>
                    <td style={{ ...td, color: C.muted, fontSize: 11.5 }}>{q.type_ar}</td>
                    <td style={{ ...td, color: q.result === "correct" ? C.ok : C.bad }}>{q.candidate_answer || "—"}</td>
                    <td style={{ ...td, color: C.ok, fontWeight: 700 }}>{q.expected}</td>
                    <td style={td}><StatusPill status={q.result} /></td>
                    <td style={{ ...td, color: q.fast_guess ? C.bad : C.muted }}>{q.time_seconds ? `${q.time_seconds}s` : "—"}</td>
                    <td style={{ ...td, fontSize: 11.5, color: C.miss }}>{notes.join(" · ") || "—"}</td>
                  </tr>
                );
              })}
              </tbody>
            </table>
        }

        {/* ── Pop-ups ── */}
        <div style={h2}>ثانياً: أسئلة البوب أب ({initial_popups.length} كلمات)</div>
        {initial_popups.length === 0
          ? <div style={{ color: C.muted, fontSize: 13 }}>لم تظهر أي نوافذ منبثقة.</div>
          : <table style={tableSty} data-testid="sheet-popup-table">
              <thead>
                <tr>
                  <th style={th}>#</th>
                  <th style={th}>النص المعروض</th>
                  <th style={th}>إجابة المرشح</th>
                  <th style={th}>الإجابة المتوقّعة</th>
                  <th style={th}>النتيجة</th>
                  <th style={th}>الوقت</th>
                </tr>
              </thead>
              <tbody>
              {initial_popups.map((p, i) => (
                <tr key={p.id} style={{ background: i % 2 ? C.rowAlt : C.paper }}>
                  <td style={td}>{p.order}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{p.title_ar}</td>
                  <td style={{ ...td, color: p.result === "correct" ? C.ok : C.bad }}>{p.candidate_answer || "—"}</td>
                  <td style={{ ...td, color: C.ok, fontWeight: 700 }}>{p.expected}</td>
                  <td style={td}><StatusPill status={p.missed ? "missing" : p.result} /></td>
                  <td style={{ ...td, color: p.fast_guess ? C.bad : C.muted }}>{p.time_seconds ? `${p.time_seconds}s` : "—"}</td>
                </tr>
              ))}
              </tbody>
            </table>
        }

        {/* ── Demo Tasks ── */}
        <div style={h2}>ثالثاً: الديمو العملي ({tasks.length} مهمة)</div>
        {tasks.length === 0
          ? <div style={{ color: C.muted, fontSize: 13 }} data-testid="sheet-no-demo">لم يصل المرشّح إلى الديمو العملي.</div>
          : tasks.map((t, idx) => (
            <div key={idx} style={{
              border: `1px solid ${C.border}`, borderRadius: 12, marginBottom: 16,
              padding: 14, background: C.rowAlt,
            }} data-testid={`sheet-task-${idx}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 12, color: C.muted, fontWeight: 800 }}>مهمة {t.order}</div>
                  <div style={{ fontSize: 16, fontWeight: 900, color: C.accentDk }}>{t.task_id} — {t.scenario}</div>
                </div>
                <div style={{ textAlign: "left", fontSize: 12 }}>
                  <div>الوقت: <b>{fmtTime(t.time_spent_seconds)}</b></div>
                  <div>الدقة: <b style={{ color: t.data_match_score >= 80 ? C.ok : t.data_match_score >= 60 ? C.miss : C.bad }}>{t.data_match_score}/100</b></div>
                  <div>الحالة: <b style={{ color: t.final_task_result === "completed" ? C.ok : C.bad }}>{t.final_task_result === "completed" ? "أُكمِلت" : "غير مكتملة"}</b></div>
                </div>
              </div>
              <div style={{ ...meta, marginBottom: 10 }}>
                <div>بدء: <span style={metaVal}>{fmtISO(t.opened_at)}</span></div>
                <div>انتهاء: <span style={metaVal}>{fmtISO(t.completed_at)}</span></div>
                <div>المستندات الناقصة: <span style={metaVal}>{t.missing_document_handling}</span></div>
                <div>الموعد: <span style={metaVal}>{t.appointment_handling}</span></div>
                <div>توليد PDF: <span style={metaVal}>{t.pdf_generated ? "نعم" : "لا"}</span></div>
                <div>رفع PDF: <span style={metaVal}>{t.pdf_uploaded ? "نعم" : "لا"}</span></div>
              </div>
              {(t.field_audit && t.field_audit.length > 0) ? (
                <table style={tableSty}>
                  <thead>
                    <tr>
                      <th style={th}>الحقل</th>
                      <th style={th}>المطلوب (من Training Portal)</th>
                      <th style={th}>ما أدخله المرشّح</th>
                      <th style={th}>النتيجة</th>
                      <th style={th}>نوع الخطأ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.field_audit.map((f, j) => (
                      <tr key={j} style={{ background: j % 2 ? C.paper : "transparent" }}>
                        <td style={{ ...td, fontWeight: 800, color: C.accentDk }}>{f.label_ar}</td>
                        <td style={{ ...td, color: C.ok, fontWeight: 700 }}>{f.expected}</td>
                        <td style={{ ...td, color: f.status === "ok" ? C.ok : C.bad, fontWeight: 700 }}>{f.entered}</td>
                        <td style={td}><StatusPill status={f.status} /></td>
                        <td style={{ ...td, color: f.status === "ok" ? C.muted : C.bad, fontSize: 12 }}>{f.error_ar || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div style={{ fontSize: 12, color: C.muted }}>
                  تفاصيل الحقول غير متوفّرة لهذه المهمّة (تم تسليمها قبل تفعيل تدقيق الحقول المُفصّل).
                </div>
              )}
            </div>
          ))
        }

        {/* ── Behaviour Log ── */}
        <div style={h2}>رابعاً: سجل السلوك أثناء الاختبار</div>
        <div style={{ ...meta, gridTemplateColumns: "1fr 1fr 1fr" }} data-testid="sheet-behaviour">
          <div>تغيير التبويب: <span style={metaVal}>{behaviour.tab_switches}</span></div>
          <div>محاولات اللصق: <span style={metaVal}>{behaviour.paste_attempts}</span></div>
          <div>محاولات التحديث: <span style={metaVal}>{behaviour.refresh_attempts}</span></div>
          <div>أحداث الخروج (blur): <span style={metaVal}>{behaviour.blur_events}</span></div>
          <div>التحذيرات: <span style={metaVal}>{behaviour.warnings}</span></div>
          <div>إجابات سريعة جداً: <span style={metaVal}>{behaviour.fast_guess_count}</span></div>
          <div>متوسط زمن الإجابة: <span style={metaVal}>{behaviour.avg_response_time}s</span></div>
          <div>هل خرج من الصفحة: <span style={{ ...metaVal, color: behaviour.left_page ? C.bad : C.ok }}>{behaviour.left_page ? "نعم" : "لا"}</span></div>
          <div>إنهاء بدون PDF: <span style={{ ...metaVal, color: behaviour.finished_without_pdf ? C.bad : C.ok }}>{behaviour.finished_without_pdf ? "نعم" : "لا"}</span></div>
        </div>
        {behaviour.flagged_questions && behaviour.flagged_questions.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 6 }}>أسئلة بسلوك مشبوه:</div>
            <ul style={{ margin: 0, paddingInlineStart: 20 }}>
              {behaviour.flagged_questions.map((f, i) => (
                <li key={i} style={{ fontSize: 12.5, color: C.bad }}>{f.title_ar} — {f.events.join(", ")}</li>
              ))}
            </ul>
          </div>
        )}

        <div style={{ marginTop: 32, paddingTop: 14, borderTop: `1px solid ${C.border}`,
                      fontSize: 11.5, color: C.muted, textAlign: "center" }}>
          هذه الورقة تُصدر آلياً من نظام ATS — مرجع داخلي للإدارة فقط.
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
        }
      `}</style>
    </div>
  );
}

const btnLight = {
  padding: "8px 14px", background: "#fff", border: `1px solid ${C.border}`,
  color: C.ink, borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer",
};
const btnAccent = {
  padding: "8px 16px", background: C.accent, border: "none",
  color: "#fff", borderRadius: 8, fontWeight: 800, fontSize: 13, cursor: "pointer",
};
const tableSty = {
  width: "100%", borderCollapse: "collapse", fontSize: 12.5, marginTop: 6,
};
const th = {
  textAlign: "right", padding: "8px 10px", background: "#f3f4f6",
  fontWeight: 800, color: C.accentDk, fontSize: 12, borderBottom: `1px solid ${C.border}`,
};
const td = {
  padding: "8px 10px", borderBottom: `1px solid ${C.border}`, color: C.ink,
  verticalAlign: "top",
};
